import React, { useState } from 'react';
import Sparkline from './Sparkline.jsx';
import Chart from './Chart.jsx';
import Benchmark from './Benchmark.jsx'
import Standing from './Standing.jsx';
import DevTable from './DevTable.jsx';
import DeclaredTable, { usableColumns } from './DeclaredTable.jsx';
import MixPanel from './MixPanel.jsx';
import HeadsPanel from './HeadsPanel.jsx';

const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  cardBg: '#1e293b',
  text: '#f1f5f9',
  textMuted: '#94a3b8',
  border: '#334155',
};

// Four panel states, four visibly different renders. Collapsing any two of them
// is the bug this whole feature is built to avoid:
//
//   ok           a number (including a real 0)
//   unavailable  the source is healthy and says it cannot compute this, + why
//   unreachable  the feed is down; we know nothing
//   missing      the view asks for a metric the source no longer publishes
//
// The one that matters most is unavailable-vs-a-real-zero. "No escapes among
// 200 deploys" and "we could not measure escapes" are opposite facts, and a
// dashboard that draws them the same way will eventually be used to make a
// decision on the wrong one.

function formatValue(value, format, precision) {
  if (value == null) return '—';
  if (typeof value !== 'number') return String(value);
  const n = value.toFixed(precision ?? 0);
  // Strip a trailing .0 so a whole number reads as one; keep genuine decimals.
  return format === 'percent' ? n : n.replace(/\.0$/, '');
}

// A feed error carries the upstream body so a developer can diagnose it. On a
// wall-mounted screen that trailing JSON is noise nobody can act on, so the
// wallboard shows only the human half. The detail stays on the desk-sized view.
function shortenError(message, wallboard) {
  if (!message) return message;
  if (!wallboard) return message;
  const cut = message.search(/[:\s]*[{[]/);
  return cut > 0 ? message.slice(0, cut).replace(/[:\s]+$/, '') : message;
}

// '2026-10-29' → 'Thu 29 Oct'. Built from the parts, not new Date(string):
// that parses as UTC midnight and shows the day before anywhere west of it.
export function formatArrives(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return iso || 'later';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  const mo = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m[2] - 1];
  return `${wd} ${+m[3]} ${mo}`;
}

function DeltaBadge({ delta, goodDirection, label, muted }) {
  if (delta == null || !Number.isFinite(delta)) return null;
  const flat = Math.abs(delta) < 0.05;
  const rising = delta > 0;
  // A metric may declare NO good direction (null): the arrow still says which
  // way it moved, but it is not coloured as good news or bad.
  const good = flat || !goodDirection ? null : (goodDirection === 'down' ? !rising : rising);
  const color = good === null ? COLORS.textMuted : good ? COLORS.success : COLORS.danger;
  // The arrow carries direction and the sign carries magnitude, so the meaning
  // survives for a colorblind reader and on a projector that mangles hue.
  const arrow = flat ? '→' : rising ? '↑' : '↓';

  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, opacity: muted ? 0.35 : 1 }}>
      <span style={{ color, fontSize: 13, fontWeight: 600 }}>
        {arrow} {flat ? '0' : `${Math.abs(delta).toFixed(1)}`}%
      </span>
      {label && <span style={{ color: COLORS.textMuted, fontSize: 11 }}>{label}</span>}
    </span>
  );
}

const cardBase = {
  background: COLORS.cardBg,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: '18px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minHeight: 132,
};

// Which reading of a tile this viewer last chose. Per browser, per panel: it is
// a reading preference, not board config, so it does not belong in the view row.
const variantStoreKey = (id) => `hub.variant.${id}`;
function storedVariant(id) {
  try {
    return window.localStorage.getItem(variantStoreKey(id));
  } catch {
    return null; // SSR, a private window, or storage blocked — the default reading
  }
}

export default function Panel({ panel: declared, wallboard = false, dimmed = false }) {
  // A source may offer the same quantity measured more than one way. The tile
  // shows one at a time and the chosen variant's fields simply replace the
  // tile's own — everything below renders a plain panel either way. A wallboard
  // has no controls, so it always shows the source's default.
  const variants = !wallboard && declared.state === 'ok' && Array.isArray(declared.variants) ? declared.variants : null;
  const [picked, setPicked] = useState(() => storedVariant(declared.id));
  const active = variants ? variants.find((v) => v.key === picked) || variants[0] : null;
  const panel = active ? { ...declared, ...active, key: declared.key, label: declared.label } : declared;
  const pick = (key) => {
    setPicked(key);
    try {
      window.localStorage.setItem(variantStoreKey(declared.id), key);
    } catch {
      /* the choice still holds for this page view */
    }
  };

  const big = wallboard;
  // A hero tile on a desk view: bigger number, taller chart. Kept separate from
  // `big` (which means "wallboard") because the wallboard deliberately shows the
  // sparkline and no benchmark, and a hero must not inherit that.
  const hero = !wallboard && (panel.size === 'xl' || panel.size === 'lg');
  const titleSize = big ? 15 : hero ? 14 : 12;
  const valueSize = big ? 52 : hero ? 46 : 34;
  const opacity = dimmed ? 0.3 : 1;

  // Which week a weekly number is for, printed on the tile itself: "This week"
  // showed the week that ended 18 Oct with no date anywhere on it.
  // A figure over several weeks names its span, ending with the newest week.
  const weekDay = panel.state === 'ok' && panel.week ? formatArrives(panel.week).replace(/^\w+ /, '') : null;
  const weekOf = weekDay && (panel.weekSpan ? `${panel.weekSpan} weeks to week of ${weekDay}` : `Week of ${weekDay}`);
  const title = (
    <div
      style={{
        fontSize: titleSize,
        color: COLORS.textMuted,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        fontWeight: 600,
      }}
    >
      {panel.title || panel.label}
      {weekOf && (
        <span style={{ marginLeft: 8, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>· {weekOf}</span>
      )}
    </div>
  );

  // ── Scheduled: on the board before its metric exists. Says when, and is
  // drawn as a plan rather than as a fault: nothing has failed yet. Past its
  // date the server stops sending this state and the tile reads missing.
  if (panel.state === 'scheduled') {
    return (
      <div style={{ ...cardBase, opacity, background: 'transparent', borderStyle: 'dashed', minHeight: 96 }}>
        {title}
        <div style={{ fontSize: big ? 22 : 16, fontWeight: 600, color: COLORS.textMuted }}>
          Arrives {formatArrives(panel.arrives)}
        </div>
      </div>
    );
  }

  // ── Unreachable: the feed is down. Say which one. ─────────────────────────
  if (panel.state === 'unreachable') {
    return (
      <div style={{ ...cardBase, opacity, borderColor: COLORS.danger, borderStyle: 'dashed' }}>
        {title}
        <div style={{ fontSize: big ? 22 : 16, fontWeight: 700, color: COLORS.danger }}>
          ⚠ Feed down
        </div>
        <div style={{ fontSize: big ? 13 : 11.5, color: COLORS.textMuted, lineHeight: 1.45 }}>
          {shortenError(panel.error, big)}
        </div>
      </div>
    );
  }

  // ── Missing: the view and the source have drifted apart. ──────────────────
  if (panel.state === 'missing') {
    return (
      <div style={{ ...cardBase, opacity, borderColor: COLORS.warning, borderStyle: 'dashed' }}>
        {title}
        <div style={{ fontSize: big ? 22 : 16, fontWeight: 700, color: COLORS.warning }}>
          ⚠ Not published
        </div>
        <div style={{ fontSize: big ? 13 : 11.5, color: COLORS.textMuted, lineHeight: 1.45 }}>
          {panel.error}
        </div>
      </div>
    );
  }

  // ── Unavailable: healthy source, honestly unmeasurable metric. ────────────
  // Rendered as neither a number nor an alarm. It is a legitimate answer, and
  // the reason is the payload — it tells you what to go fix.
  if (panel.state === 'unavailable') {
    return (
      <div style={{ ...cardBase, opacity, background: 'transparent', borderStyle: 'dashed' }}>
        {title}
        <div style={{ fontSize: big ? 30 : 22, fontWeight: 700, color: COLORS.textMuted }}>
          Not measurable
        </div>
        <div style={{ fontSize: big ? 13 : 11.5, color: COLORS.textMuted, lineHeight: 1.45 }}>
          {panel.reason}
        </div>
        {/* Still show the field. "We cannot measure our change failure rate,
            and here is where the industry sits" is a more useful tile than a
            blank one — it keeps the gap visible instead of tidy. */}
        {!wallboard && <Standing standing={panel.standing} />}
        {!wallboard && <Benchmark benchmark={panel.benchmark} />}
      </div>
    );
  }

  // ── A mix metric: a composition, not a scalar. Shown on the wallboard too —
  // unlike the per-person table, a share-of-work bar reads fine across a room.
  if (panel.format === 'mix' && Array.isArray(panel.rows)) {
    return (
      <div style={{ ...cardBase, opacity }}>
        {title}
        <MixPanel rows={panel.rows} note={wallboard ? null : panel.note} />
      </div>
    );
  }

  // ── Where the heads went: team shares and org heads, with its own toggles.
  if (panel.format === 'heads' && panel.value && typeof panel.value === 'object' && !Array.isArray(panel.value)) {
    return (
      <div style={{ ...cardBase, opacity }}>
        {title}
        <HeadsPanel value={panel.value} note={wallboard ? null : panel.note} />
      </div>
    );
  }

  // ── A table metric: rows, not a scalar. Never on a wallboard — a per-person
  // table is read at a desk, not across a room, and a named list on a TV is a
  // different artefact from a metric.
  if (panel.format === 'table' && Array.isArray(panel.rows)) {
    if (wallboard) return null;
    // A source that declares its own columns gets the generic renderer; one that
    // declares nothing — or sends a spec we cannot use — keeps the per-developer
    // epic layout this board shipped with, so an older source is untouched.
    const declared = usableColumns(panel.columns);
    // An empty list is an answer ("nothing committed yet"), not a broken tile:
    // say so, rather than leave a card with a title and nothing under it.
    if (panel.rows.length === 0) {
      return (
        <div style={{ ...cardBase, opacity }}>
          {title}
          <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.textMuted }}>{panel.emptyText || 'None yet'}</div>
          {panel.caption && (
            <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.45 }}>{panel.caption}</div>
          )}
          {panel.note && (
            <div style={{ fontSize: 11.5, color: COLORS.textMuted, lineHeight: 1.45 }}>{panel.note}</div>
          )}
        </div>
      );
    }
    return (
      <div style={{ ...cardBase, opacity }}>
        {title}
        {panel.caption && (
          <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.45 }}>{panel.caption}</div>
        )}
        {declared ? (
          <DeclaredTable rows={panel.rows} columns={declared} note={panel.note} ordered={panel.ordered} />
        ) : (
          <DevTable rows={panel.rows} note={panel.note} />
        )}
      </div>
    );
  }

  // ── A sentence: the line a tab opens on, read as prose rather than as a
  // number with a unit. Its note says what it is built from.
  if (panel.kind === 'sentence' && typeof panel.value === 'string') {
    return (
      <div style={{ ...cardBase, opacity, minHeight: 0 }}>
        <div style={{ fontSize: big ? 26 : 19, lineHeight: 1.45, color: COLORS.text, fontWeight: 500, maxWidth: 900 }}>
          {panel.value}
        </div>
        {panel.note && !wallboard && (
          <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.4 }}>{panel.note}</div>
        )}
      </div>
    );
  }

  // ── OK: a real number. ────────────────────────────────────────────────────
  return (
    <div style={{ ...cardBase, opacity }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        {title}
        {variants && (
          <div style={{ display: 'flex', gap: 0 }} role="group" aria-label="Measured in">
            {variants.map((v, i) => {
              const on = v.key === active.key;
              return (
                <button
                  key={v.key}
                  onClick={() => pick(v.key)}
                  aria-pressed={on}
                  style={{
                    background: on ? COLORS.primary : 'transparent',
                    border: `1px solid ${on ? COLORS.primary : COLORS.border}`,
                    borderLeftWidth: i === 0 || on ? 1 : 0,
                    borderRadius: i === 0 ? '6px 0 0 6px' : i === variants.length - 1 ? '0 6px 6px 0' : 0,
                    color: on ? '#fff' : COLORS.textMuted,
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: on ? 600 : 400,
                    cursor: 'pointer',
                  }}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: valueSize, fontWeight: 700, lineHeight: 1, color: COLORS.text }}>
          {formatValue(panel.value, panel.format, panel.precision)}
        </span>
        {panel.unit && (
          <span style={{ fontSize: big ? 18 : 14, color: COLORS.textMuted, fontWeight: 500 }}>
            {panel.unit}
          </span>
        )}
      </div>

      <DeltaBadge
        delta={panel.delta}
        goodDirection={panel.goodDirection}
        label={panel.deltaLabel}
        muted={dimmed}
      />

      {/* What the number means to someone who does not think in its unit. Sized
          to be read, unlike the note: this is the line an exec takes away. */}
      {panel.caption && !wallboard && (
        <div style={{ fontSize: hero ? 15 : 13, color: COLORS.text, lineHeight: 1.45, maxWidth: 760 }}>
          {panel.caption}
        </div>
      )}

      {/* Is it good: the goal, our own history, and where to look when not. */}
      {!wallboard && <Standing standing={panel.standing} />}

      {/* On a WALLBOARD keep the sparkline: the chart's week/month tabs are
          controls, and nobody is standing close enough to a TV to press one.
          Everywhere else the full chart is the point. */}
      {wallboard
        ? panel.trend && panel.trend.length > 1 && (
            <Sparkline
              points={panel.trend}
              color={COLORS.primary}
              width={big ? 200 : 132}
              height={big ? 46 : 34}
              muted={dimmed}
            />
          )
        : (panel.trend?.length > 1 || panel.monthly?.length > 1) && (
            <Chart
              key={active ? active.key : 'only'}
              weekly={panel.trend}
              monthly={panel.monthly}
              quarterly={panel.quarterly}
              monthlyAgg={panel.monthlyAgg}
              unit={panel.unit}
              breaks={panel.breaks}
              height={panel.size === 'xl' || panel.size === 'lg' ? 340 : 260}
            />
          )}

      {panel.note && (
        <div style={{ fontSize: big ? 12.5 : 11, color: COLORS.textMuted, lineHeight: 1.4 }}>
          {panel.note}
        </div>
      )}

      {/* Industry context is for reading, not for a TV across the room. */}
      {!wallboard && <Benchmark benchmark={panel.benchmark} />}
    </div>
  );
}
